import { useState, useEffect, useRef, useCallback } from 'react';
import { Send, MessageCircle, Loader2, X } from 'lucide-react';
import { useBazaarMessagingHub } from '../hooks/useSignalR';
import { bazaarMessagesService, createApiClient } from '../services/api';
import type { BazaarChatMessage } from '../types';
import { UserAvatar } from './UserAvatar';

interface BazaarChatPanelProps {
  listingId: string;
  otherPartyId: string;
  otherPartyName: string;
  currentUserName?: string;
  getToken: () => Promise<string>;
  onClose: () => void;
}

/** Listing-scoped buyer <-> seller chat — mirrors MessagingPanel's Job chat, minus voice/AI-improve. */
export function BazaarChatPanel({
  listingId, otherPartyId, otherPartyName, currentUserName = 'Me', getToken, onClose,
}: BazaarChatPanelProps) {
  const [messages, setMessages] = useState<(BazaarChatMessage & { isMine: boolean })[]>([]);
  const [input, setInput] = useState('');
  const [sending, setSending] = useState(false);
  const [loading, setLoading] = useState(true);
  const bottomRef = useRef<HTMLDivElement>(null);

  const inThread = useCallback((msg: BazaarChatMessage) =>
    msg.senderId === otherPartyId || msg.recipientId === otherPartyId, [otherPartyId]);

  useEffect(() => {
    setLoading(true);
    bazaarMessagesService(createApiClient(getToken)).getMessages(listingId, otherPartyId)
      .then(all => setMessages(all.filter(inThread).map(m => ({ ...m, isMine: m.isMine ?? m.senderId !== otherPartyId }))))
      .catch(console.error)
      .finally(() => setLoading(false));
  }, [listingId, otherPartyId, inThread, getToken]);

  const onNew = useCallback((msg: BazaarChatMessage) => {
    if (!inThread(msg)) return;
    const enriched = { ...msg, isMine: msg.isMine ?? msg.senderId !== otherPartyId };
    setMessages(prev => prev.find(m => m.id === enriched.id) ? prev : [...prev, enriched]);
  }, [inThread, otherPartyId]);

  useBazaarMessagingHub({ getToken, listingId, onMessageReceived: onNew });

  useEffect(() => { bottomRef.current?.scrollIntoView({ behavior: 'smooth' }); }, [messages]);

  const handleSend = async () => {
    const content = input.trim();
    if (!content || sending) return;
    setSending(true);
    setInput('');
    try {
      const msg = await bazaarMessagesService(createApiClient(getToken)).sendMessage(listingId, content, otherPartyId);
      setMessages(prev => {
        const exists = prev.find(m => m.id === msg.id);
        if (exists) return prev.map(m => m.id === msg.id ? { ...m, isMine: true } : m);
        return [...prev, { ...msg, isMine: true }];
      });
    } catch { setInput(content); }
    finally { setSending(false); }
  };

  return (
    <div data-testid="bazaar-chat-panel" className="bg-white border border-gray-200 rounded-xl flex flex-col h-96 shadow-lg">
      <div className="px-4 py-3 border-b border-gray-100 flex items-center gap-2 shrink-0">
        <MessageCircle className="w-4 h-4 text-blue-600" />
        <span className="font-medium text-gray-900 text-sm">Chat with {otherPartyName}</span>
        <div className="ml-auto flex items-center gap-2">
          <div className="w-2 h-2 bg-green-500 rounded-full" title="Live" />
          <button data-testid="bazaar-chat-close" onClick={onClose} className="text-gray-400 hover:text-gray-600">
            <X className="w-4 h-4" />
          </button>
        </div>
      </div>

      <div className="flex-1 overflow-y-auto px-4 py-3 space-y-3">
        {loading ? (
          <div className="flex justify-center items-center h-full">
            <Loader2 className="w-5 h-5 animate-spin text-gray-400" />
          </div>
        ) : messages.length === 0 ? (
          <div className="flex flex-col items-center justify-center h-full text-center">
            <MessageCircle className="w-8 h-8 text-gray-200 mb-2" />
            <p className="text-sm text-gray-400">No messages yet. Say hello!</p>
          </div>
        ) : (
          messages.map(msg => (
            <div key={msg.id} data-testid={`bazaar-chat-message-${msg.id}`} className={`flex items-end gap-2 ${msg.isMine ? 'flex-row-reverse' : 'flex-row'}`}>
              <UserAvatar name={msg.isMine ? currentUserName : otherPartyName} size="xs" className="mb-4 shrink-0" />
              <div className="flex flex-col max-w-[72%]">
                <div className={`rounded-2xl px-3.5 py-2 text-sm leading-relaxed ${
                  msg.isMine ? 'bg-blue-600 text-white rounded-tr-sm' : 'bg-gray-100 text-gray-900 rounded-tl-sm'
                }`}>
                  {msg.content}
                </div>
                <span className={`text-xs text-gray-400 mt-1 px-1 ${msg.isMine ? 'text-right' : 'text-left'}`}>
                  {new Date(msg.sentAt).toLocaleTimeString('en-GB', { hour: '2-digit', minute: '2-digit' })}
                </span>
              </div>
            </div>
          ))
        )}
        <div ref={bottomRef} />
      </div>

      <div className="px-4 py-3 border-t border-gray-100 shrink-0 flex items-end gap-2">
        <textarea
          data-testid="bazaar-chat-input"
          value={input}
          onChange={e => setInput(e.target.value)}
          onKeyDown={e => { if (e.key === 'Enter' && !e.shiftKey) { e.preventDefault(); handleSend(); } }}
          rows={2}
          placeholder="Type a message (Enter to send)"
          className="flex-1 text-sm border border-gray-300 rounded-lg px-3 py-2 focus:ring-2 focus:ring-blue-500 focus:border-transparent resize-none"
        />
        <button
          data-testid="bazaar-chat-send"
          onClick={handleSend}
          disabled={!input.trim() || sending}
          className="bg-blue-600 hover:bg-blue-700 disabled:bg-blue-300 text-white p-2.5 rounded-lg transition-colors shrink-0"
        >
          {sending ? <Loader2 className="w-4 h-4 animate-spin" /> : <Send className="w-4 h-4" />}
        </button>
      </div>
    </div>
  );
}
